/* eslint-disable react/jsx-handler-names */
/* eslint-disable camelcase */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames";
import Portal from "components/atoms/Portal";
import PageTitle from "components/atoms/PageTitle";
import useTranslate from "hooks/useTranslate";
import { useDialogWindowStore } from "../../../stores/dialogWindow";

import "./styles.sass";

interface IDialogWindow {
  classNamesProps?: string | string[];
  classNamesContentProps?: string | string[];
  children: React.ReactNode;
  title: string;
  size?: "notAuth" | "auth" | "full";
  balance?: string;
  method?: string;
  isOpened?: boolean;
  onBack?: (() => void) | null;
  onClose?: (() => void) | null;
  onBackClose?: (() => void) | null;
  hideHeader?: boolean;
  isShowTabbar?: boolean;
}

const DialogWindow: React.FC<IDialogWindow> = ({
  classNamesProps,
  children,
  size = "auth",
  title,
  balance,
  method,
  hideHeader,
  isShowTabbar,
  onBack,
  onClose,
  onBackClose,
  classNamesContentProps,
}) => {
  const t = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const [isRender, setIsRender] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const isOpen = useDialogWindowStore((state) => state.isOpen);

  const [{ x }, api] = useSpring(() => ({
    x: window.innerWidth,
  }));

  useEffect(() => {
    if (location.pathname === "/profile") {
      setIsRender(true);
      return;
    }
    if (isOpen) {
      setIsRender(true);
    } else {
      api.start({ x: window.innerWidth });
      setTimeout(() => {
        setIsRender(false);
        useDialogWindowStore.getState().setIsOpen(false);
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const close = () => {
    api.start({ x: window.innerWidth });
    setTimeout(() => {
      useDialogWindowStore.getState().setIsDialogWindowOpen(false);
      setIsRender(false);
      if (onClose) {
        onClose();
      } else {
        navigate(location.pathname);
      }
    }, 200);
  };

  const closeOnBack = () => {
    api.start({ x: window.innerWidth });
    setTimeout(() => {
      useDialogWindowStore.getState().setIsDialogWindowOpen(false);
      setIsRender(false);
      if (onBackClose) {
        onBackClose();
      }
    }, 200);
  };

  const open = () => {
    api.start({ x: 0 });
    useDialogWindowStore.getState().setIsDialogWindowOpen(true);
  };

  useEffect(() => {
    open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop } = contentRef.current;
        setIsScrolled(scrollTop > 0);
      }
    };

    const observeContent = () => {
      const contentElement = contentRef.current;
      if (contentElement) {
        contentElement.addEventListener("scroll", handleScroll);
      }

      return () => {
        if (contentElement) {
          contentElement.removeEventListener("scroll", handleScroll);
        }
      };
    };

    const observer = new MutationObserver(() => {
      if (contentRef.current) {
        observeContent();
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const currentContentRef = contentRef.current;

    return () => {
      observer.disconnect();
      if (currentContentRef) {
        currentContentRef.removeEventListener("scroll", handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dialogWindowClass = classNames("DialogWindow", classNamesProps, {
    DialogWindow__auth: size === "auth",
    DialogWindow__notAuth: size === "notAuth",
    DialowWindow__full: size === "full",
    DialogWindow__show_tabbar: !!isShowTabbar,
    DialowWindow__nottabbar:
      location.search.includes("invite") || location.search.includes("profile"),
  });

  const dialogWindowClassContent = classNames(
    {
      DialogWindow__content: true,
      DialogWindow__content_auth: size === "auth",
      DialogWindow__content_notAuth: size === "notAuth",
      DialogWindow__content_full: size === "full",
      DialogWindow__content_nottabbar:
        location.search.includes("invite") ||
        location.search.includes("profile"),
      DialogWindow__content_padding: hideHeader,
    },
    classNamesContentProps,
  );

  const HeaderClass = classNames("DialogWindow__header", {
    border: isScrolled,
  });

  const titleValue =
    balance && !method ? `${t("payment.header.balance")} ${balance}` : title;

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isRender && (
        <Portal>
          <animated.div className={dialogWindowClass} style={{ x }}>
            <div className="DialogWindow__wrapper">
              {!hideHeader ? (
                <div className={HeaderClass}>
                  <PageTitle
                    title={titleValue}
                    onBack={onBack || (onBackClose ? closeOnBack : close)}
                    onClose={onClose ? close : null}
                  />
                </div>
              ) : null}
              <div
                className={dialogWindowClassContent}
                ref={contentRef}
                style={{ overflowY: "auto", maxHeight: "80vh" }}
              >
                {children}
              </div>
            </div>
          </animated.div>
        </Portal>
      )}
    </>
  );
};

export default DialogWindow;
